import { createUseStyles } from 'react-jss'
import {
  addDisplayName,
  addEffect,
  addHandlers,
  addProps,
  addRef,
  addState,
  flowMax,
} from 'ad-hok'
import { FC } from 'react'
import { Brand as CmsBrand } from 'shared/src/api/types'
import { addDataLoadingFromPromise } from 'shared/src/utils/dataLoading'
import { addApi, addUser } from 'shared/src/api'
import { typedAs } from 'shared/src/utils/typedAs'
import {
  addDebouncedCopy,
  addEffectOnMount,
  addPropTrackingRef,
} from 'ad-hok-utils'
import cx from 'classnames'
import { addOptionalIntPathParam } from 'shared/src/utils/addNavigate'

import { addTranslationHelpers } from 'utils/addTranslationHelpers'
import { Modal } from '../Modal'
import { BODY_MEDIUM_STYLE, NOTES_BOLD_STYLE } from '../styles'
import { IconButton } from '../IconButton'
import { CreateNewButton } from '../CreateNewButton'
import { addToastContext } from '../toastContext'
import { addCmsLoadingAndErrorIndicators } from 'utils/addCmsLoadingAndErrorIndicators'

const SIDE_PADDING = 24
const useStyles = createUseStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    background: 'white',
    paddingLeft: SIDE_PADDING,
    paddingRight: SIDE_PADDING,
    paddingTop: 26,
    height: '100dvh',
  },
  header: {
    display: 'flex',
    gap: 8,
    marginBottom: 32,
    flexShrink: 0,
    height: 26,
  },
  close: {
    position: 'relative',
    right: -10,
    alignSelf: 'center',
  },
  closeIcon: {
    color: '#90734F',
  },
  input: {
    ...BODY_MEDIUM_STYLE,
    flexGrow: 1,
    caretColor: '#90734F',
    outline: 'none',
    color: 'rgba(54, 52, 50, 0.96)',

    '&::placeholder': {
      color: 'rgba(144, 115, 79, 0.56)',
      opacity: 1,
    },
  },
  row: {
    ...NOTES_BOLD_STYLE,
    display: 'flex',
    alignItems: 'center',
    color: 'rgba(54, 52, 50, 0.8)',
    paddingTop: 17,
    paddingBottom: 17,
    paddingLeft: 16 + SIDE_PADDING,
    paddingRight: 16 + SIDE_PADDING,
  },
  createNewButton: {
    marginBottom: 32,
    flexShrink: 0,
  },
  brandsContainer: {
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    paddingBottom: 32,
    alignSelf: 'center',
    width: `calc(100% + ${2 * SIDE_PADDING}px)`,
  },
})

export type Brand = Pick<CmsBrand, 'id' | 'name'>

interface RowProps {
  brand: CmsBrand
  onClick: () => void
}

const Row: FC<RowProps> = flowMax(
  addDisplayName('Row'),
  addProps(() => ({
    classes: useStyles(),
  })),
  ({ brand: { name }, onClick, classes }) => (
    <button onClick={onClick} className={classes.row}>
      {name}
    </button>
  )
)

const getNormalizedBrandName = (brandName: string): string =>
  brandName.toLowerCase().trim()

interface CreateNewBrandButtonProps {
  name: string
  normalizedExistingBrandNames: Set<string>
  onSelect: (brand: Brand) => void
}

const CreateNewBrandButton: FC<CreateNewBrandButtonProps> = flowMax(
  addDisplayName('CreateNewBrandButton'),
  addTranslationHelpers,
  addApi,
  addToastContext,
  addState('isSaving', 'setIsSaving', false),
  addHandlers({
    onClick:
      ({ name, api, onSelect, showErrorToast, setIsSaving, t }) =>
      async () => {
        try {
          setIsSaving(true)
          const brand = await api.brands.findOrCreateByName({
            name,
            domain: null,
          })

          onSelect(brand)
        } catch (e) {
          showErrorToast({
            title: t('admin.product.errorCreatingBrand'),
            details: t('admin.general.genericError'),
          })
        } finally {
          setIsSaving(false)
        }
      },
  }),
  addProps(({ name, normalizedExistingBrandNames, isSaving, t }) => ({
    disabled:
      !name.trim() ||
      normalizedExistingBrandNames.has(getNormalizedBrandName(name)) ||
      isSaving,
    label: name
      ? t('admin.product.createNewBrandName', { name })
      : t('admin.product.createNewBrand'),
  })),
  addProps(() => ({
    classes: useStyles(),
  })),
  ({ label, disabled, onClick, classes }) => (
    <CreateNewButton
      onClick={onClick}
      label={label}
      disabled={disabled}
      className={classes.createNewButton}
    />
  )
)

const getNormalizedExistingBrandNames = (
  brands: { name: string }[]
): Set<string> => new Set(brands.map(({ name }) => name.toLowerCase().trim()))

interface Props {
  onSelect: (brand: Brand) => void
  onCancel: () => void
}

export const BrandSearch: FC<Props> = flowMax(
  addDisplayName('BrandSearch'),
  addApi,
  addUser,
  addOptionalIntPathParam('userId'),
  addDataLoadingFromPromise(
    'data',
    ({ userId, user, api }) =>
      Promise.all([
        api.brands.getAllUsed(),
        api.brands.mostRecentForUser(userId ?? user.id),
      ]),
    []
  ),
  addCmsLoadingAndErrorIndicators('data'),
  addProps(({ data: [brands, userBrands] }) => ({
    brands,
    userBrands,
  })),
  addState('searchResults', 'setSearchResults', typedAs<CmsBrand[]>([])),
  addState('searchText', 'onChangeSearchText', ''),
  addDebouncedCopy(200, 'searchText', 'searchTextDebounced'),
  addPropTrackingRef('searchText', 'searchTextRef'),
  addEffect(
    ({
        searchTextDebounced,
        api,
        searchTextRef,
        setSearchResults,
        userBrands,
      }) =>
      () => {
        if (!searchTextDebounced && userBrands.length > 0) {
          setSearchResults(userBrands)
          return
        }

        api.brands.searchUsed(searchTextDebounced).then((results) => {
          if (searchTextRef.current !== searchTextDebounced) return

          setSearchResults(results)
        })
      },
    ['searchTextDebounced']
  ),
  addHandlers({
    onChange:
      ({ onChangeSearchText }) =>
      (event: React.ChangeEvent<HTMLInputElement>) => {
        onChangeSearchText(event.target.value)
      },
  }),
  addProps(
    ({ brands }) => ({
      normalizedExistingBrandNames: getNormalizedExistingBrandNames(brands),
    }),
    ['brands']
  ),
  addRef('inputRef', typedAs<HTMLInputElement | null>(null)),
  addEffectOnMount(({ inputRef }) => () => {
    inputRef.current!.focus()
  }),
  addHandlers({
    onClickSearchIcon:
      ({ inputRef }) =>
      () => {
        inputRef.current!.focus()
      },
  }),
  addProps(() => ({
    classes: useStyles(),
  })),
  addTranslationHelpers,
  ({
    inputRef,
    searchText,
    searchResults,
    onClickSearchIcon,
    onChange,
    onSelect,
    onCancel,
    classes,
    normalizedExistingBrandNames,
    t,
  }) => (
    <Modal containerClassName={classes.container}>
      <div className={classes.header}>
        <IconButton name="search" onClick={onClickSearchIcon} />
        <input
          placeholder={t('admin.post.searchBrand')}
          onChange={onChange}
          ref={inputRef}
          className={cx(classes.input, 'focus:ring-0')}
        />
        <IconButton
          name="close"
          onClick={onCancel}
          className={classes.close}
          iconClassName={classes.closeIcon}
          addHitSlop
        />
      </div>
      <CreateNewBrandButton
        name={searchText}
        normalizedExistingBrandNames={normalizedExistingBrandNames}
        onSelect={onSelect}
      />
      <div className={classes.brandsContainer}>
        {searchResults.map((brand) => (
          <Row
            brand={brand}
            onClick={() => {
              onSelect(brand)
            }}
            key={brand.id}
          />
        ))}
      </div>
    </Modal>
  )
)
